import { useCallback, useMemo } from 'react';
import { useSuiClient } from '@mysten/dapp-kit';
import { useQuery } from '@tanstack/react-query';
import {
	referralRepository,
	ReferralRegistry,
	ReferrerData,
	RefereeData,
	ReferrerBalance,
	RefereeBalance,
	ReferralLevelReward,
} from '@/repositories';
import { APP_CFG } from '@/configs';
import { CAMPAIGN_CFG } from '@/configs/campaign-config';
import { useAuth } from '@/hooks';
import { ensureSuiClient } from '@/utils/sui-compat';

const HOUSE_EDGE_RATE = 0.01; // 1%
const PLATFORM_SHARE_RATE = 0.4; // 40%
const COMMISSION_SCALING_FACTOR = HOUSE_EDGE_RATE * PLATFORM_SHARE_RATE;

type ReferralSnapshot = {
	registry: ReferralRegistry | null;
	referrerData: ReferrerData | null;
	refereeData: RefereeData | null;
	referrerBalances: ReferrerBalance[];
	refereeBalances: RefereeBalance[];
	levelUpRewards: ReferralLevelReward[];
};

const REFERRAL_DISABLED = {
	isLoading: false,
	error: null,
	registry: null,
	referrerData: null,
	refereeData: null,
	referrerBalances: [] as ReferrerBalance[],
	refereeBalances: [] as RefereeBalance[],
	levelUpRewards: [] as ReferralLevelReward[],
	refetch: async () => {},
	generateReferralLink: () => '',
	totalEarnings: 0,
	pendingAmount: 0,
	commissionRate: 0,
	refereeRate: 0,
	defaultCommissionRate: 0,
	defaultRefereeRate: 0,
	hasCustomCommissionRate: false,
	hasCustomRefereeRate: false,
	effectiveCommissionRate: 0,
	effectiveDefaultCommissionRate: 0,
	isReferrer: false,
	isReferee: false,
	totalReferees: 0,
	referredBy: null as string | null,
	referredDate: null as Date | null,
} as const;

const EMPTY_REFERRAL_SNAPSHOT: ReferralSnapshot = {
	registry: null,
	referrerData: null,
	refereeData: null,
	referrerBalances: [],
	refereeBalances: [],
	levelUpRewards: [],
};

const toMarketingRate = (rawRatePercent: number): number => {
	if (!rawRatePercent || rawRatePercent <= 0) return 0;
	return rawRatePercent / COMMISSION_SCALING_FACTOR;
};

/**
 * Custom hook for managing referral data from the blockchain
 */
export function useReferralData() {
	const referralEnabled = CAMPAIGN_CFG.features.referral.enabled !== false;

	const client = ensureSuiClient(useSuiClient());
	const { address } = useAuth();

	const referralQuery = useQuery({
		queryKey: ['referralData', address],
		queryFn: async (): Promise<ReferralSnapshot> => {
			if (!address || !referralEnabled) {
				return EMPTY_REFERRAL_SNAPSHOT;
			}

			const [registry, referrerData, refereeData] = await Promise.all([
				referralRepository.getReferralRegistry(client),
				referralRepository.getReferrerData(client, address),
				referralRepository.getRefereeData(client, address),
			]);

			const coinTypes = [APP_CFG.SUI_COIN_TYPE, APP_CFG.USDC_COIN_TYPE].filter(Boolean) as string[];

			const [referrerBalances, refereeBalances] = await Promise.all([
				Promise.all(coinTypes.map((coinType) => referralRepository.getReferrerBalance(client, address, coinType))).then((values) =>
					values.filter(Boolean),
				),
				Promise.all(coinTypes.map((coinType) => referralRepository.getRefereeBalance(client, address, coinType))).then((values) =>
					values.filter(Boolean),
				),
			]);

			return {
				registry,
				referrerData,
				refereeData,
				referrerBalances: referrerBalances as ReferrerBalance[],
				refereeBalances: refereeBalances as RefereeBalance[],
				levelUpRewards: registry?.levelUpUsdRewardsPerReferee ?? [],
			};
		},
		enabled: referralEnabled && Boolean(address),
		staleTime: 60_000,
		refetchOnWindowFocus: true,
	});

	const snapshot = referralQuery.data ?? EMPTY_REFERRAL_SNAPSHOT;
	const registry = snapshot.registry;
	const referrerData = snapshot.referrerData;
	const refereeData = snapshot.refereeData;
	const referrerBalances = snapshot.referrerBalances;
	const refereeBalances = snapshot.refereeBalances;
	const levelUpRewards = snapshot.levelUpRewards;
	const error = referralQuery.error instanceof Error ? referralQuery.error.message : null;

	// Generate referral link
	const generateReferralLink = useCallback(
		(customCode?: string) => {
			if (!address) return '';
			if (typeof window === 'undefined') return '';

			// Use custom code or address as referral identifier
			const referralId = customCode || address;
			return `${window.location.origin}?play=${referralId}`;
		},
		[address],
	);

	const totals = useMemo(() => {
		const referrerTotal = referrerBalances.reduce((sum, balance) => sum + balance.totalEarnedCommissionUsd, 0);
		const refereeTotal = refereeBalances.reduce((sum, balance) => sum + balance.totalEarnedUsd, 0);
		const levelUpTotal = referrerData?.totalEarnedLevelUpUsdRewards ?? 0;
		const totalEarnings = referrerTotal + refereeTotal + levelUpTotal;

		const referrerPending = referrerBalances.reduce((sum, balance) => sum + balance.commissionBalance, 0);
		const refereePending = refereeBalances.reduce((sum, balance) => sum + balance.balance, 0);
		const levelUpPending = referrerData?.usdRewardsBalance ?? 0;
		const pendingAmount = referrerPending + refereePending + levelUpPending;

		return {
			totalEarnings,
			pendingAmount,
		};
	}, [refereeBalances, referrerBalances, referrerData?.totalEarnedLevelUpUsdRewards, referrerData?.usdRewardsBalance]);

	const effectiveCommissionRate = useMemo(() => {
		const specialRate = referrerData?.specialCommissionRate ?? 0;
		const defaultRate = registry?.defaultReferrerRate ?? 0;
		return specialRate > 0 ? specialRate : defaultRate;
	}, [referrerData?.specialCommissionRate, registry?.defaultReferrerRate]);

	const effectiveDefaultCommissionRate = registry?.defaultReferrerRate ?? 0;
	const marketingCommissionRate = toMarketingRate(effectiveCommissionRate);
	const marketingDefaultCommissionRate = toMarketingRate(effectiveDefaultCommissionRate);

	const refereeRate = useMemo(() => {
		const specialRate = referrerData?.specialRefereeRate;
		if (specialRate !== undefined && specialRate > 0) {
			return specialRate;
		}
		return registry?.defaultRefereeRate ?? 0;
	}, [referrerData?.specialRefereeRate, registry?.defaultRefereeRate]);

	const isReferrer = Boolean(referrerData && referrerData.numberOfReferee > 0);
	const isReferee = Boolean(refereeData);

	if (!referralEnabled) {
		return REFERRAL_DISABLED;
	}

	return {
		// State
		isLoading: referralQuery.isLoading,
		error,
		registry,
		referrerData,
		refereeData,
		referrerBalances,
		refereeBalances,
		levelUpRewards,

		// Actions
		refetch: () => (address ? referralQuery.refetch() : Promise.resolve()),
		generateReferralLink,

		// Computed values
		totalEarnings: totals.totalEarnings,
		pendingAmount: totals.pendingAmount,
		commissionRate: marketingCommissionRate,
		refereeRate,
		defaultCommissionRate: marketingDefaultCommissionRate,
		defaultRefereeRate: registry?.defaultRefereeRate ?? 0,
		hasCustomCommissionRate: (referrerData?.specialCommissionRate ?? 0) > 0,
		hasCustomRefereeRate: (referrerData?.specialRefereeRate ?? 0) > 0,
		effectiveCommissionRate,
		effectiveDefaultCommissionRate,
		isReferrer,
		isReferee,

		// User stats
		totalReferees: referrerData?.numberOfReferee || 0,
		referredBy: refereeData?.referrer,
		referredDate: refereeData?.dateReferredTimestamp ? new Date(refereeData.dateReferredTimestamp) : null,
	};
}
